'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MapPinIcon, PencilIcon, StarIcon, Trash2Icon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import AddressDialog from './address-dialog';
import { deleteAddress, setAddressPrimary } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { AddressType } from '@/types/address';
type PropsType = {
  address: AddressType;
};
const AddressCard = t0 => {
  const $ = _c(110);
  const {
    address
  } = t0;
  const t = useTranslations();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  let t1;
  if ($[0] !== address.id) {
    t1 = () => setAddressPrimary(address.id);
    $[0] = address.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== queryClient || $[3] !== t) {
    t2 = async () => {
      await queryClient.invalidateQueries({
        queryKey: ["addresses"]
      });
      toast.success(t("address_set_primary_success"));
    };
    $[2] = queryClient;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = error => toast.error(getErrorMessage(error, t("something_went_wrong")));
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: t3
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    mutate: setPrimary,
    isPending: settingPrimary
  } = useMutation(t4);
  let t5;
  if ($[11] !== address.id) {
    t5 = () => deleteAddress(address.id);
    $[11] = address.id;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== queryClient || $[14] !== t) {
    t6 = async () => {
      await queryClient.invalidateQueries({
        queryKey: ["addresses"]
      });
      toast.success(t("address_deleted_success"));
      setDeleteOpen(false);
    };
    $[13] = queryClient;
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t) {
    t7 = error_0 => toast.error(getErrorMessage(error_0, t("something_went_wrong")));
    $[16] = t;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = {
      mutationFn: t5,
      onSuccess: t6,
      onError: t7
    };
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const {
    mutate: remove,
    isPending: deleting
  } = useMutation(t8);
  const t9 = address.city?.name;
  const t10 = address.state?.name;
  const t11 = address.country?.name;
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = [t9, t10, t11].filter(Boolean);
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const locationParts = t12;
  let t13;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <MapPinIcon className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== address.title) {
    t14 = <div className="flex items-center gap-2">{t13}<span className="font-semibold text-gray-900">{address.title}</span></div>;
    $[27] = address.title;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== address.primary || $[30] !== t) {
    t15 = address.primary && <Badge variant="default" className="bg-brand shrink-0 text-xs text-white">{t("primary")}</Badge>;
    $[29] = address.primary;
    $[30] = t;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t14 || $[33] !== t15) {
    t16 = <div className="flex items-start justify-between gap-2">{t14}{t15}</div>;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== locationParts) {
    t17 = locationParts.length > 0 && <p className="text-muted-foreground text-sm">{locationParts.join(", ")}</p>;
    $[35] = locationParts;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== address.zipCode || $[38] !== t) {
    t18 = address.zipCode && <p className="text-sm text-gray-600"><span className="text-muted-foreground">{t("zip_code")}: </span>{address.zipCode}</p>;
    $[37] = address.zipCode;
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== address.text) {
    t19 = address.text && <p className="text-sm text-gray-700">{address.text}</p>;
    $[40] = address.text;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== address.primary || $[43] !== setPrimary || $[44] !== settingPrimary || $[45] !== t) {
    t20 = !address.primary && <Button size="sm" variant="outline" disabled={settingPrimary} onClick={() => setPrimary()} className="text-xs"><StarIcon className="mr-1 h-3 w-3" />{t("set_as_primary")}</Button>;
    $[42] = address.primary;
    $[43] = setPrimary;
    $[44] = settingPrimary;
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setEditOpen(true);
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <PencilIcon className="mr-1 h-3 w-3" />;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t) {
    t23 = t("edit");
    $[49] = t;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t23) {
    t24 = <Button size="sm" variant="outline" onClick={t21} className="text-xs">{t22}{t23}</Button>;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  let t26;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setDeleteOpen(true);
    t26 = <Trash2Icon className="mr-1 h-3 w-3" />;
    $[53] = t25;
    $[54] = t26;
  } else {
    t25 = $[53];
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t) {
    t27 = t("delete");
    $[55] = t;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== t27) {
    t28 = <Button size="sm" variant="outline" className="text-destructive hover:text-destructive border-destructive/30 hover:border-destructive text-xs" onClick={t25}>{t26}{t27}</Button>;
    $[57] = t27;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== t20 || $[60] !== t24 || $[61] !== t28) {
    t29 = <div className="mt-1 flex flex-wrap items-center gap-2 border-t pt-3">{t20}{t24}{t28}</div>;
    $[59] = t20;
    $[60] = t24;
    $[61] = t28;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== t16 || $[64] !== t17 || $[65] !== t18 || $[66] !== t19 || $[67] !== t29) {
    t30 = <div className="flex flex-col gap-3 rounded-lg border bg-white p-4 shadow-sm transition-shadow hover:shadow-md">{t16}{t17}{t18}{t19}{t29}</div>;
    $[63] = t16;
    $[64] = t17;
    $[65] = t18;
    $[66] = t19;
    $[67] = t29;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== address || $[70] !== editOpen) {
    t31 = <AddressDialog address={address} open={editOpen} onOpenChange={setEditOpen} />;
    $[69] = address;
    $[70] = editOpen;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== t) {
    t32 = t("delete_address");
    $[72] = t;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  let t33;
  if ($[74] !== t32) {
    t33 = <DialogHeader><DialogTitle>{t32}</DialogTitle></DialogHeader>;
    $[74] = t32;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  let t34;
  if ($[76] !== address.title || $[77] !== t) {
    t34 = t("delete_address_confirm", {
      title: address.title
    });
    $[76] = address.title;
    $[77] = t;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== t34) {
    t35 = <ScrollArea className="min-h-0 flex-1" viewportClassName="max-h-[calc(90vh-9rem)]"><div className="px-5 pr-3 pb-4"><p className="text-muted-foreground text-sm">{t34}</p></div></ScrollArea>;
    $[79] = t34;
    $[80] = t35;
  } else {
    t35 = $[80];
  }
  let t36;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = () => setDeleteOpen(false);
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== t) {
    t37 = t("cancel");
    $[82] = t;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== deleting || $[85] !== t37) {
    t38 = <Button variant="outline" onClick={t36} disabled={deleting}>{t37}</Button>;
    $[84] = deleting;
    $[85] = t37;
    $[86] = t38;
  } else {
    t38 = $[86];
  }
  let t39;
  if ($[87] !== remove) {
    t39 = () => remove();
    $[87] = remove;
    $[88] = t39;
  } else {
    t39 = $[88];
  }
  let t40;
  if ($[89] !== deleting || $[90] !== t) {
    t40 = deleting ? t("deleting") : t("delete");
    $[89] = deleting;
    $[90] = t;
    $[91] = t40;
  } else {
    t40 = $[91];
  }
  let t41;
  if ($[92] !== deleting || $[93] !== t39 || $[94] !== t40) {
    t41 = <Button variant="destructive" onClick={t39} disabled={deleting}>{t40}</Button>;
    $[92] = deleting;
    $[93] = t39;
    $[94] = t40;
    $[95] = t41;
  } else {
    t41 = $[95];
  }
  let t42;
  if ($[96] !== t38 || $[97] !== t41) {
    t42 = <DialogFooter className="gap-2">{t38}{t41}</DialogFooter>;
    $[96] = t38;
    $[97] = t41;
    $[98] = t42;
  } else {
    t42 = $[98];
  }
  let t43;
  if ($[99] !== t33 || $[100] !== t35 || $[101] !== t42) {
    t43 = <DialogContent aria-describedby={undefined} className="sm:max-w-sm">{t33}{t35}{t42}</DialogContent>;
    $[99] = t33;
    $[100] = t35;
    $[101] = t42;
    $[102] = t43;
  } else {
    t43 = $[102];
  }
  let t44;
  if ($[103] !== deleteOpen || $[104] !== t43) {
    t44 = <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>{t43}</Dialog>;
    $[103] = deleteOpen;
    $[104] = t43;
    $[105] = t44;
  } else {
    t44 = $[105];
  }
  let t45;
  if ($[106] !== t30 || $[107] !== t31 || $[108] !== t44) {
    t45 = <>{t30}{t31}{t44}</>;
    $[106] = t30;
    $[107] = t31;
    $[108] = t44;
    $[109] = t45;
  } else {
    t45 = $[109];
  }
  return t45;
};
export default AddressCard;